import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    const maxAttempts = 6;
    const pollInterval = 2000;

    const poll = async (attempt) => {
      if (attempt >= maxAttempts) {
        setStatus("timeout");
        return;
      }
      try {
        const { data } = await API.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.stripe_status === "expired") {
          setStatus("failed");
          return;
        }
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), pollInterval);
      } catch {
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), pollInterval);
      }
    };

    poll(0);
  };

  return (
    <div className="min-h-screen pt-20 pb-12 bg-[#F9FAFB] flex items-center justify-center" data-testid="payment-success-page">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white border border-border p-8 text-center">
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 size={48} className="text-[#0A3FA3] mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-bold mb-2">{t("payment.checking")}</h2>
              <p className="text-sm text-muted-foreground">Attempt {attempts + 1}/6</p>
            </div>
          )}
          {status === "success" && (
            <div data-testid="payment-success">
              <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">{t("payment.success")}</h2>
              <p className="text-sm text-muted-foreground mb-6">{t("payment.success_msg")}</p>
              <Link to="/dashboard">
                <Button className="bg-[#0A3FA3] hover:bg-[#082F7A] rounded-sm" data-testid="payment-go-dashboard">{t("payment.go_dashboard")}</Button>
              </Link>
            </div>
          )}
          {(status === "failed" || status === "timeout") && (
            <div data-testid="payment-failed">
              <XCircle size={48} className="text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">{t("payment.failed")}</h2>
              <p className="text-sm text-muted-foreground mb-6">{t("payment.failed_msg")}</p>
              <Link to="/dashboard">
                <Button variant="outline" className="rounded-sm" data-testid="payment-failed-dashboard">{t("payment.go_dashboard")}</Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
